import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wallet, History, Heart, Star, Sparkles, MessageCircle, CalendarClock } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useWallet } from "../context/WalletContext";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { RgpdPanel } from "../components/RgpdPanel";

const STATUS_LABEL = {
  pending: "En attente", active: "En cours", completed: "Terminée",
  refused: "Refusée", cancelled: "Annulée",
};

export default function ConsultantDashboard() {
  const { user } = useAuth();
  const { balance } = useWallet();
  const [consultations, setConsultations] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [bookings, setBookings] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.listConsultations().then(({ data }) => setConsultations(data)).catch(() => {});
    api.favorites().then(({ data }) => setFavorites(data)).catch(() => {});
    api.myBookings().then(({ data }) => setBookings(data)).catch(() => {});
  }, []);

  const cancelBooking = async (bid) => { try { await api.cancelBooking(bid); const { data } = await api.myBookings(); setBookings(data); } catch {} };

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
      <h1 className="font-serif text-3xl text-celeste-ivoire">Bonjour {user.display_name} ✦</h1>
      <p className="mt-1 text-sm text-celeste-lilas/70">Votre espace de guidance personnel.</p>

      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <div className="glass rounded-3xl border border-celeste-or/20 p-5">
          <Wallet className="h-6 w-6 text-celeste-or" />
          <p className="mt-2 font-serif text-3xl text-celeste-or">{balance ?? "…"}</p>
          <p className="text-sm text-celeste-lilas/70">crédits disponibles</p>
          <Button data-testid="dash-recharge-btn" onClick={() => navigate("/credits")} className="mt-3 w-full gold-btn font-semibold">Recharger</Button>
        </div>
        <div className="glass rounded-3xl p-5">
          <MessageCircle className="h-6 w-6 text-celeste-or" />
          <p className="mt-2 font-serif text-3xl text-celeste-ivoire">{consultations.length}</p>
          <p className="text-sm text-celeste-lilas/70">consultations</p>
        </div>
        <div className="glass rounded-3xl p-5">
          <Heart className="h-6 w-6 text-celeste-or" />
          <p className="mt-2 font-serif text-3xl text-celeste-ivoire">{favorites.length}</p>
          <p className="text-sm text-celeste-lilas/70">praticiens favoris</p>
        </div>
      </div>

      <Tabs defaultValue="history" className="mt-8">
        <TabsList className="bg-celeste-night/50">
          <TabsTrigger value="history" data-testid="tab-history" className="data-[state=active]:bg-celeste-violet/30">Historique</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="tab-bookings" className="data-[state=active]:bg-celeste-violet/30">Rendez-vous</TabsTrigger>
          <TabsTrigger value="favorites" data-testid="tab-favorites" className="data-[state=active]:bg-celeste-violet/30">Favoris</TabsTrigger>
          <TabsTrigger value="data" data-testid="tab-data" className="data-[state=active]:bg-celeste-violet/30">Mes données</TabsTrigger>
        </TabsList>

        <TabsContent value="history" className="mt-4">
          <div className="glass rounded-3xl p-2 sm:p-4" data-testid="consultation-history">
            {consultations.length === 0 ? (
              <p className="p-6 text-center text-sm text-celeste-lilas/60">Aucune consultation pour l'instant. <span onClick={() => navigate("/experts")} className="cursor-pointer text-celeste-or">Trouvez un expert →</span></p>
            ) : (
              <div className="divide-y divide-celeste-violet/15">
                {consultations.map((c) => (
                  <div key={c.id} className="flex items-center justify-between gap-3 px-3 py-3">
                    <div className="flex items-center gap-3">
                      <img src={c.expert_photo} alt="" className="h-10 w-10 rounded-full object-cover" />
                      <div>
                        <p className="text-sm text-celeste-ivoire">{c.expert_name}</p>
                        <p className="text-xs text-celeste-lilas/50">{new Date(c.created_at).toLocaleString("fr-FR")}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="rounded-full bg-celeste-violet/20 px-2 py-0.5 text-xs text-celeste-lilas">{STATUS_LABEL[c.status]}</span>
                      {c.status === "completed" && <span className="text-xs text-celeste-or">{Math.round(c.credits_spent * 100) / 100} cr</span>}
                      {["pending", "active"].includes(c.status) && (
                        <Button size="sm" className="gold-btn" onClick={() => navigate(`/chat/${c.id}`)}>Rejoindre</Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="bookings" className="mt-4">
          <div className="glass rounded-3xl p-2 sm:p-4" data-testid="bookings-list">
            {bookings.length === 0 ? (
              <p className="p-6 text-center text-sm text-celeste-lilas/60">Aucun rendez-vous. Réservez un créneau depuis le profil d'un praticien.</p>
            ) : (
              <div className="divide-y divide-celeste-violet/15">
                {bookings.map((b) => (
                  <div key={b.id} className="flex items-center justify-between gap-3 px-3 py-3">
                    <div className="flex items-center gap-3">
                      <img src={b.expert_photo} alt="" className="h-10 w-10 rounded-full object-cover" />
                      <div>
                        <p className="text-sm text-celeste-ivoire">{b.expert_name}</p>
                        <p className="text-xs text-celeste-lilas/50">{new Date(b.scheduled_at).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" })}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="rounded-full bg-celeste-violet/20 px-2 py-0.5 text-xs text-celeste-lilas">{b.status === "booked" ? "Confirmé" : b.status === "cancelled" ? "Annulé" : b.status}</span>
                      {b.status === "booked" && <Button data-testid={`cancel-booking-${b.id}`} size="sm" variant="ghost" className="border border-red-400/40 text-red-200" onClick={() => cancelBooking(b.id)}>Annuler</Button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="favorites" className="mt-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="favorites-list">
            {favorites.length === 0 ? (
              <p className="col-span-full p-6 text-center text-sm text-celeste-lilas/60">Aucun favori.</p>
            ) : favorites.map((f) => (
              <div key={f.id} className="glass card-hover flex items-center gap-3 rounded-2xl p-4">
                <img src={f.photo_url} alt="" className="h-14 w-14 rounded-full object-cover" />
                <div className="flex-1">
                  <p className="text-celeste-ivoire">{f.professional_name}</p>
                  <p className="flex items-center gap-1 text-xs text-celeste-or"><Star className="h-3 w-3 fill-celeste-or" /> {f.rating?.toFixed(1)}</p>
                </div>
                <Button size="sm" variant="ghost" className="border border-celeste-violet/30 text-celeste-lilas" onClick={() => navigate(`/experts/${f.id}`)}>Voir</Button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="data" className="mt-4">
          <RgpdPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
